import { FC } from 'react';
import { useQueryClient } from 'react-query';
import { TaskCenterTypes, useTaskCenterMutations, taskCenterQueryKeys } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  Button,
  Text,
  useAlert,
  ConfirmationModal,
  useModalControl,
} from '@frontend/design-system';
import { useTaskCenterStore } from '../../provider';
import { taskCenterTrackingIds } from '../../tracking-ids';

interface TaskDetailsPopoverMenuProps {
  id: string;
  status: TaskCenterTypes.TaskStatus;
  onClickEdit: () => void;
}

export const TaskDetailsPopoverMenu: FC<TaskDetailsPopoverMenuProps> = ({ id, status, onClickEdit }) => {
  const { t } = useTranslation('task-center');
  const { setTaskMode, setSelectedTask } = useTaskCenterStore(['setSelectedTask', 'setTaskMode']);
  const { taskDeleteMutation } = useTaskCenterMutations();
  const { getItemProps, getMenuProps, getTriggerProps, close } = usePopoverMenu();
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { openModal, modalProps } = useModalControl();

  const deleteTask = () => {
    taskDeleteMutation.mutate(
      { id },
      {
        onError: () => {
          alert.error(t('Failed to delete task'));
        },
        onSuccess: () => {
          setTaskMode(null);
          setSelectedTask(null);
          queryClient.invalidateQueries(taskCenterQueryKeys[status]);
        },
      }
    );
  };

  return (
    <div>
      <Button
        iconName='edit'
        variant='secondary'
        trackingId={taskCenterTrackingIds.editTaskFromDetails}
        onClick={onClickEdit}
        hoverLabel={t('Edit Task')}
      />
      <Button
        iconName='more'
        variant='secondary'
        {...getTriggerProps()}
        trackingId={taskCenterTrackingIds.openTaskDetailsOptions}
        hoverLabel={t('More options')}
      />
      <PopoverMenu {...getMenuProps()}>
        <PopoverMenuItem
          {...getItemProps()}
          onClick={() => {
            close();
            openModal();
          }}
          trackingId={taskCenterTrackingIds.deleteTaskFromDetails}
        >
          <Icon name='trash' size={16} color='error' />
          <Text color='error'>{t('Delete Task')}</Text>
        </PopoverMenuItem>
      </PopoverMenu>
      <ConfirmationModal
        title={t('Are you sure you want to delete the task?')}
        onConfirm={deleteTask}
        destructive
        {...modalProps}
      />
    </div>
  );
};
